import React, { useContext, useEffect } from 'react';
import { UeElement } from '@oneaudi/falcon-tools';
import { Headline } from '@oneaudi/unified-web-components';
import { useMediaQuery } from '@oneaudi/feature-app-utils';
import { Context } from '../../../context/Context';
import { useTrackingManager } from '../../../utils/useTrackingManager';

import {
  SubmenuTriggerWrapper,
  HeadlineWrapper,
  SubmenuTriggerButton,
  StyledSubheadline,
  StyledParagraph,
} from '../SubPageNavigationStyles';
import { ArrowIcon } from './ArrowIcon';
import { handleSubmenuFocus, trapFocusWithDelay } from '../../../utils/a11y';

const SubmenuTrigger: React.FC = () => {
  const { navigation } = useTrackingManager();
  const {
    content: { links },
    headline,
    setPopOverState,
    hasLinks,
    hasCta,
    submenuDesktopRef,
    submenuMobileRef,
    submenuTriggerRef,
    showSubmenu,
    toggleShowSubmenu,
    showSubheadline,
    triggerId,
    submenuId,
  } = useContext(Context);
  const mobileView = useMediaQuery('(max-width: 767px)');
  const subheadline =
    links
      ?.filter((item) => item.currentPage)
      .map((item) => item.text)
      .toString() || '';

  const handleNavClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setPopOverState(false);

    const mobileDevice = window.innerWidth < 1024;

    toggleShowSubmenu();

    const submenuRef = mobileDevice ? submenuMobileRef.current : submenuDesktopRef.current;
    const triggerRef = submenuTriggerRef.current;

    if (!submenuRef || !triggerRef) return;

    if (!showSubmenu) {
      trapFocusWithDelay(submenuRef, triggerRef, 100);
    }
    navigation(showSubmenu);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const mobileDevice = window.innerWidth < 1024;
    if (event.key === 'Escape' && showSubmenu) {
      setPopOverState(false);
      if (
        (submenuDesktopRef?.current?.contains(document.activeElement) ||
          submenuMobileRef?.current?.contains(document.activeElement)) &&
        submenuTriggerRef.current
      ) {
        event.preventDefault();
        submenuTriggerRef.current.focus();
      }
      toggleShowSubmenu();
    }

    if (
      (event.key === 'Enter' || event.key === 'ArrowDown' || event.key === ' ') &&
      event.target === submenuTriggerRef.current
    ) {
      event.preventDefault();
      if (mobileDevice) {
        // Handle mobile submenu
        handleSubmenuFocus(
          submenuMobileRef,
          submenuTriggerRef,
          showSubmenu,
          toggleShowSubmenu,
          100,
        );
      } else {
        // Handle desktop submenu
        handleSubmenuFocus(
          submenuDesktopRef,
          submenuTriggerRef,
          showSubmenu,
          toggleShowSubmenu,
          30,
        );
      }
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showSubmenu]);

  return (
    <SubmenuTriggerWrapper>
      <SubmenuTriggerButton
        onClick={(e) => handleNavClick(e)}
        ref={submenuTriggerRef}
        aria-haspopup="menu"
        aria-expanded={showSubmenu}
        aria-controls={submenuId}
        id={triggerId}
        aria-labelledby={`headline${hasLinks && showSubheadline ? ' subheadline' : ''}`}
      >
        <HeadlineWrapper>
          {mobileView ? (
            <StyledParagraph
              tag="p"
              variant={{ type: 'copy', order: '2', style: 'bold' }}
              id="headline"
              role="presentation"
            >
              {headline}
            </StyledParagraph>
          ) : (
            <Headline
              tag="h4"
              variant={{ type: 'headline', order: '4', weight: 'normal' }}
              className="headline"
              id="headline"
              role="presentation"
            >
              <UeElement type="text" property="headline" label="Main Page Title">
                {headline}
              </UeElement>
            </Headline>
          )}
          {hasLinks && showSubheadline && (
            <StyledSubheadline
              variant={{ type: 'copy', order: '2', style: 'normal' }}
              id="subheadline"
              tag="span"
              role="presentation"
            >
              {subheadline}
            </StyledSubheadline>
          )}
        </HeadlineWrapper>
        {(hasLinks || (mobileView && hasCta)) && <ArrowIcon rotated={showSubmenu} />}
      </SubmenuTriggerButton>
    </SubmenuTriggerWrapper>
  );
};

export default SubmenuTrigger;
